/**
 * Acting-as context for View As (Beta-1 plan P4-06 / P4-07, Tim C0814 Mailchimp-style support mode).
 *
 * `DemoRoleContext` publishes who is at the keyboard and whose lens is active; services call
 * `guardWrite(action, details)` before a mutation:
 *   - read-only View As (org setting `viewAs.writeAccess[viewerRole] === false`) → throws
 *     ReadOnlyViewAsError so the UI can say "Read only" instead of writing;
 *   - full-access View As → records `admin_acted_as` in Hugo_ActivityLog + a bell notification
 *     for the impersonated member ("Tim Autrey (Administrator) made a change on your behalf: …");
 *     the email side is WF8b/WF11 (P7-06) reading the same docs.
 * Module-level state on purpose: services are plain functions with no React context.
 */
import { addDoc, collection, Timestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { logActivity } from '../utils/firestore';

export class ReadOnlyViewAsError extends Error {
  constructor(message = 'You are viewing as another member in read-only mode — changes are disabled.') {
    super(message);
    this.name = 'ReadOnlyViewAsError';
    this.code = 'view-as/read-only';
  }
}

const state = {
  actingFor: null, // { uid, name, role } — the impersonated member
  enteredBy: null, // { uid, name, email, roleLabel } — the person at the keyboard
  readOnly: false,
  organizationId: null,
};

export function setActingContext(next) {
  Object.assign(state, {
    actingFor: next?.actingFor || null,
    enteredBy: next?.enteredBy || null,
    readOnly: Boolean(next?.readOnly),
    organizationId: next?.organizationId || null,
  });
}

export function getActingContext() {
  return { ...state };
}

export function isReadOnlyViewAs() {
  return Boolean(state.actingFor && state.readOnly);
}

/** Default write access per viewer role level (Beta-1: full access ON for every level, per Tim). */
export const DEFAULT_VIEW_AS_WRITE_ACCESS = Object.freeze({
  org_admin: true,
  org_it: true,
  executive: true,
  site_manager: true,
  department_lead: true,
  team_lead: true,
});

/** Is View As read-only for this viewer role under the org's setting? (missing = default ON) */
export function viewAsIsReadOnly(org, viewerRole) {
  const setting = org?.viewAs?.writeAccess || {};
  const allowed = setting[viewerRole] ?? DEFAULT_VIEW_AS_WRITE_ACCESS[viewerRole] ?? true;
  return allowed === false;
}

/** Human line for the audit trail / notification. */
export function describeActedAs(action, details = {}) {
  const what = details.summary || String(action || 'made a change').replace(/_/g, ' ');
  return what;
}

/**
 * Call before any client-side mutation. Throws when read-only; otherwise, under View As, records
 * the act for the impersonated member (fire-and-forget) and returns the acting context so the
 * caller can stamp `actingFor` / `enteredBy` on the document.
 * @param {string} action   e.g. 'survey_response', 'organization_update', 'threshold_update'
 * @param {object} details  { summary, ...anything useful } — summary is what the member reads
 */
export function guardWrite(action, details = {}) {
  if (!state.actingFor) return { actingFor: null, enteredBy: state.enteredBy };
  if (state.readOnly) throw new ReadOnlyViewAsError();
  recordActedAs(action, details).catch(() => {});
  return { actingFor: state.actingFor, enteredBy: state.enteredBy };
}

/** The audit entry + bell notification for the impersonated member (P4-07). */
export async function recordActedAs(action, details = {}) {
  const { actingFor, enteredBy, organizationId } = state;
  if (!actingFor || !enteredBy) return;
  const summary = describeActedAs(action, details);
  const by = `${enteredBy.name || enteredBy.email || 'An administrator'}${enteredBy.roleLabel ? ` (${enteredBy.roleLabel})` : ''}`;
  const message = `${by} made a change on your behalf: ${summary}`;
  try {
    await logActivity(enteredBy.uid, 'admin_acted_as', 'drift-accumulation', {
      actingFor: { uid: actingFor.uid, name: actingFor.name || '' },
      enteredBy: { uid: enteredBy.uid, name: enteredBy.name || '' },
      action,
      organizationId: details.organizationId || organizationId || null,
      ...details,
    });
  } catch {
    /* activity log is best-effort */
  }
  try {
    await addDoc(collection(db, 'Hugo_Notifications'), {
      userId: actingFor.uid,
      type: 'admin_acted_as',
      message,
      link: details.link || null,
      organizationId: details.organizationId || organizationId || null,
      conditionId: details.conditionId || null,
      trlId: details.trlId || null,
      enteredBy: { uid: enteredBy.uid, name: enteredBy.name || '', email: enteredBy.email || '' },
      read: false,
      createdAt: Timestamp.now(),
    });
  } catch {
    /* bell is best-effort */
  }
}

/** Stamp for documents written under View As: { actingFor, enteredBy } or {} when not impersonating. */
export function actingStamp() {
  if (!state.actingFor) return {};
  return {
    actingFor: { uid: state.actingFor.uid, name: state.actingFor.name || '' },
    enteredBy: state.enteredBy ? { uid: state.enteredBy.uid, name: state.enteredBy.name || '' } : null,
  };
}
